import {Show} from 'platform/foundation';

import {isTruthy} from 'ramda-adjunct';

import {PINNED_RIGHT_EDGE_LINE_STYLE, ROW_BORDER_BOTTOM} from '../../constants/borderStyles';
import {DEFAULT_COL_WIDTH} from '../../constants/defaultColWidth';
import {SELECTION_COLUMN_WIDTH} from '../../constants/selectionColumn';
import type {CellRendererType} from '../../types/EagColumn';
import type {EagColumnState} from '../../types/EagColumnState';
import {getSkeletonBarClass} from './getSkeletonBarClass';

export type SkeletonRowProps = {
  heightPx: number;
  columnStates: EagColumnState[];
  /** Maps column key → renderer type so skeleton bars can vary in width/shape by column type. */
  columnTypeMap: Map<string, CellRendererType>;
  horizontalSeparators: boolean;
  actionColumnWidth?: number;
  rowSelectMode: 'SINGLE' | 'MULTIPLE' | 'NONE';
};

/**
 * Renders skeleton placeholder cells for either the left-pinned/unpinned columns
 * (`rightPinned === false`) or the right-pinned columns (`rightPinned === true`),
 * matching GridRow's left/right column split around the filler cell.
 */
function renderSkeletonColumnCells(
  columnStates: EagColumnState[],
  rightPinned: boolean,
  horizontalSeparators: boolean,
  columnTypeMap: Map<string, CellRendererType>
) {
  return columnStates
    .filter((cs) => (rightPinned ? cs.pinned === 'RIGHT' : cs.pinned !== 'RIGHT'))
    .map((colState) => {
      const width = colState.width ?? DEFAULT_COL_WIDTH;
      return (
        <td
          key={colState.key}
          className="p-2 border-0"
          style={{width, maxWidth: width, ...(horizontalSeparators ? ROW_BORDER_BOTTOM : {})}}
        >
          <div className={getSkeletonBarClass(columnTypeMap.get(colState.key))} />
        </td>
      );
    });
}

export function SkeletonRow(props: SkeletonRowProps) {
  return (
    <tr
      style={{
        height: props.heightPx,
        contentVisibility: 'auto',
        containIntrinsicSize: `auto ${props.heightPx}px`,
      }}
    >
      {/* checkbox pseudo-column placeholder — matches GridRow's RowSelectCheckbox <td>
          so the skeleton aligns with the colgroup that includes a checkbox <col>. */}
      <Show when={props.rowSelectMode !== 'NONE'}>
        <td
          className="p-0 border-0"
          style={{
            width: SELECTION_COLUMN_WIDTH,
            ...(props.horizontalSeparators ? ROW_BORDER_BOTTOM : {}),
          }}
        />
      </Show>
      {/* left-pinned + unpinned skeleton cells */}
      {renderSkeletonColumnCells(
        props.columnStates,
        false,
        props.horizontalSeparators,
        props.columnTypeMap
      )}
      {/* filler td: absorbs remaining space between unpinned and right-pinned columns */}
      <td style={props.horizontalSeparators ? ROW_BORDER_BOTTOM : undefined} />
      {/* right-pinned skeleton cells */}
      {renderSkeletonColumnCells(
        props.columnStates,
        true,
        props.horizontalSeparators,
        props.columnTypeMap
      )}
      {/* empty action column placeholder — maintains layout consistency with data rows */}
      <Show when={isTruthy(props.actionColumnWidth)}>
        <td
          style={{
            position: 'sticky' as const,
            right: 0,
            zIndex: 'var(--z-sidebar)',
            width: props.actionColumnWidth,
            maxWidth: props.actionColumnWidth,
            ...(props.horizontalSeparators ? ROW_BORDER_BOTTOM : {}),
          }}
          className="p-0 border-0 bg-white"
        >
          <span style={PINNED_RIGHT_EDGE_LINE_STYLE} />
        </td>
      </Show>
    </tr>
  );
}
